import { getBrowserApiBase } from "./browser-api-base";

export interface RiskFlowEconHistoryRow {
  id: string;
  date: string | null;
  actual: number | null;
  forecast: number | null;
  previous: number | null;
  surprise: number | null;
  direction: "beat" | "miss" | "inline" | null;
  ivScore: number | null;
}

interface RiskFlowEconHistoryResponse {
  ticker: string;
  history: RiskFlowEconHistoryRow[];
  scoring: unknown[];
  fetchedAt: string;
}

const API_BASE = getBrowserApiBase();
const historyCache = new Map<string, Promise<RiskFlowEconHistoryRow[]>>();

export async function fetchRiskFlowEconHistory(
  key: string,
): Promise<RiskFlowEconHistoryRow[]> {
  const normalized = key.trim().toUpperCase();
  if (!/^[A-Z0-9]{2,12}$/.test(normalized)) return [];

  const cached = historyCache.get(normalized);
  if (cached) return cached;

  const request = fetch(
    `${API_BASE}/api/data/econ-history/${encodeURIComponent(normalized)}?limit=8`,
    { signal: AbortSignal.timeout(6000) },
  )
    .then((response) => (response.ok ? response.json() : null))
    .then((json: RiskFlowEconHistoryResponse | null) =>
      parseHistoryRows(json?.history),
    )
    .catch(() => []);

  historyCache.set(normalized, request);
  return request;
}

function parseHistoryRows(value: unknown): RiskFlowEconHistoryRow[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      if (!item || typeof item !== "object") return null;
      const record = item as Record<string, unknown>;
      const id = typeof record.id === "string" ? record.id : null;
      if (!id) return null;
      return {
        id,
        date: typeof record.date === "string" ? record.date : null,
        actual: finite(record.actual),
        forecast: finite(record.forecast),
        previous: finite(record.previous),
        surprise: finite(record.surprise),
        direction: parseDirection(record.direction),
        ivScore: finite(record.ivScore),
      };
    })
    .filter((row): row is RiskFlowEconHistoryRow => Boolean(row));
}

function parseDirection(value: unknown): RiskFlowEconHistoryRow["direction"] {
  return value === "beat" || value === "miss" || value === "inline"
    ? value
    : null;
}

function finite(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}
